import axios from "axios";
import { APIRoutes } from "../../../../generated/from-api/api-constants/route-constants";
import { API_BASE_URL } from "../../constants";

export interface PlatformSnapshot {
  date: string;
  totalAccounts: number;
  paidAccounts: number;
  totalQuestions: number;
  totalUsers: number;
  topTiers: { tier: string; count: number }[];
}

export interface AnalyticsResponse {
  current: PlatformSnapshot;
  snapshots: PlatformSnapshot[];
}

export interface CustomerHistoryEntry {
  date: string;
  email: string;
  questions: number;
  users: number;
  tier: string | null;
  payment: string | null;
  teamSetup: string | null;
}

export interface CustomerDetailResponse {
  email: string;
  history: CustomerHistoryEntry[];
}

export interface EngagementRow {
  email: string;
  questionsAllTime: number;
  questions7d: number;
  questions60d: number;
  questions90d: number;
  questionsPrev90d: number;
  questions1y: number;
  totalUsers: number;
  totalCoaches: number;
  totalPlayers: number;
  lastCoachActivityAt: string | null;
  lastPlayerActivityAt: string | null;
  shopifyPaidTotal: number;
  shopifyLink: "yes" | "suggested" | "no";
  hubspotLink: "yes" | "suggested" | "no";
  teamCity: string | null;
  teamState: string | null;
  // Communication counts (HubSpot historical + this platform's send log).
  // commsOutbound: SendGrid sends + HubSpot OUTBOUND emails/calls
  // commsInbound:  HubSpot INBOUND emails/calls (platform inbound = 0
  //                until SendGrid Inbound Parse + Twilio inbound wired)
  // commsLogged:   HubSpot notes + meetings (no direction)
  commsOutbound?: number;
  commsInbound?: number;
  commsLogged?: number;
  commsTotal?: number;
}

export interface HubspotContact {
  hubspotId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  companyName: string | null;
  jobTitle: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  lifecycleStage: string | null;
  hubspotOwnerId: string | null;
  hubspotCreatedAt: string | null;
  hubspotUpdatedAt: string | null;
}

export interface HubspotCompany {
  hubspotId: string;
  name: string | null;
  domain: string | null;
  industry: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  phone: string | null;
}

export interface HubspotDeal {
  hubspotId: string;
  name: string | null;
  amount: number | null;
  stage: string | null;
  pipelineId: string | null;
  closeDate: string | null;
  hubspotCreatedAt: string | null;
  hubspotUpdatedAt: string | null;
}

export interface HubspotCustomerSummary {
  email: string;
  contacts: HubspotContact[];
  companies: HubspotCompany[];
  deals: HubspotDeal[];
  stats: {
    contactCount: number;
    companyCount: number;
    dealCount: number;
    openDealAmount: number;
    closedWonAmount: number;
  };
}

export interface HubspotEngagement {
  hubspotId: string;
  engagementType: string; // 'note' | 'call' | 'meeting' | 'email' | 'task'
  occurredAt: string | null;
  hubspotOwnerId: string | null;
  subject: string | null;
  body: string | null;
  direction: string | null;
  durationMs: number | null;
  outcome: string | null;
  hubspotCreatedAt: string | null;
}

export interface HubspotEngagementListResponse {
  email: string;
  total: number;
  rows: HubspotEngagement[];
}

export interface SyncOverview {
  shopify: {
    totalCustomers: number;
    confirmed: number;
    suggested: number;
    rejected: number;
    unmatched: number;
    paidUnmatchedRevenue: number;
  };
  hubspot: {
    totalContacts: number;
    confirmed: number;
    suggested: number;
    rejected: number;
    unmatched: number;
  };
}

export interface MatchCandidate {
  linkId: string;
  licenseEmail: string;
  matchPercent: number;
  matchType: string;
  matchReason: string;
  license: {
    name: string | null;
    teamName: string | null;
    teamLevel: string | null;
    city: string | null;
    state: string | null;
    questionsAllTime: number;
    totalUsers: number;
    dateCreated: string | null;
    lastCoachActivityAt: string | null;
    lastPlayerActivityAt: string | null;
  };
}

export interface MatchGroup {
  sourceType: "shopify" | "hubspot";
  sourceKind: "customer" | "contact";
  sourceId: string;
  external: {
    email: string | null;
    name: string | null;
    phone: string | null;
    company: string | null;
    city: string | null;
    state: string | null;
    paidTotal?: number;
    paidOrderCount?: number;
    lifecycleStage?: string | null;
  };
  candidates: MatchCandidate[];
}

export interface ShopifyPlatformStats {
  totalCustomers: number;
  totalPayingCustomers: number;
  totalOrders: number;
  totalPaidOrders: number;
  lifetimeCashCollected: number;
  past12moCash: number;
  averageOrderValue: number;
  monthlyRevenue: { month: string; revenue: number; orderCount: number }[];
}

export interface HubspotPlatformStats {
  totalContacts: number;
  totalCompanies: number;
  totalDeals: number;
  contactsLinkedToTNS: number;
  contactsLinkedPercent: number;
  contactsByLifecycleStage: { stage: string; count: number }[];
  closedWonValue: number;
  openPipelineValue: number;
  dealsByStage: { stage: string; count: number; amount: number }[];
}

export interface SuggestedMatch {
  linkId: string;
  sourceType: "shopify" | "hubspot";
  sourceKind: "customer" | "contact";
  sourceId: string;
  licenseEmail: string;
  matchType: string;
  matchScore: number;
  matchReason: string;
  external: {
    email: string | null;
    name: string | null;
    phone: string | null;
    company: string | null;
    paidTotal?: number;
    paidOrderCount?: number;
  };
  license: {
    email: string;
    name: string | null;
    teamName: string | null;
  };
}

export interface EngagementResponse {
  rows: EngagementRow[];
}

export interface DailyPoint {
  date: string;
  count: number;
}

export interface PlatformTimeseries {
  questionsPerDay: DailyPoint[];
  accountsPerDay: DailyPoint[];
}

export interface CustomerTimeseries {
  email: string;
  questionsPerDay: DailyPoint[];
  usersAddedPerDay: DailyPoint[];
}

export interface FunnelStage {
  id: string;
  label: string;
  count: number;
  percentOfPrevStage: number;
  percentOfTotal: number;
}

export interface FunnelResult {
  windowDays: number;
  cohortStartDate: string;
  cohortSize: number;
  stages: FunnelStage[];
  computedAt: string;
}

export interface ShopifyLineItem {
  shopifyId: string;
  title: string | null;
  quantity: number;
  price: number | null;
  sku: string | null;
  vendor: string | null;
}

export interface ShopifyOrder {
  shopifyId: string;
  orderNumber: number | null;
  email: string | null;
  customerShopifyId: string | null;
  financialStatus: string | null;
  fulfillmentStatus: string | null;
  totalPrice: number | null;
  subtotalPrice: number | null;
  totalTax: number | null;
  totalDiscounts: number | null;
  totalShippingPrice: number | null;
  currency: string | null;
  tags: string | null;
  cancelledAt: string | null;
  processedAt: string | null;
  shopifyCreatedAt: string | null;
  lineItems: ShopifyLineItem[];
}

export interface ShopifyCustomer {
  shopifyId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  ordersCount: number;
  totalSpent: number;
  currency: string | null;
  acceptsMarketing: boolean;
  tags: string | null;
  shopifyCreatedAt: string | null;
}

export interface ShopifyCustomerSummary {
  email: string;
  customer: ShopifyCustomer | null;
  orders: ShopifyOrder[];
  stats: {
    totalOrders: number;
    paidOrders: number;
    totalSpent: number;
    firstOrderDate: string | null;
    lastOrderDate: string | null;
    currency: string | null;
  };
}

export const AdminAnalyticsService = {
  GET_ANALYTICS: async function (): Promise<AnalyticsResponse> {
    return (
      await axios.get<AnalyticsResponse>(
        `${API_BASE_URL}${APIRoutes.ADMIN_ANALYTICS}`
      )
    ).data;
  },
  GET_CUSTOMER: async function (
    email: string
  ): Promise<CustomerDetailResponse> {
    return (
      await axios.get<CustomerDetailResponse>(
        `${API_BASE_URL}${APIRoutes.ADMIN_CUSTOMER}/${encodeURIComponent(
          email
        )}`
      )
    ).data;
  },
  GET_ENGAGEMENT: async function (): Promise<EngagementResponse> {
    return (
      await axios.get<EngagementResponse>(
        `${API_BASE_URL}${APIRoutes.ADMIN_ENGAGEMENT}`
      )
    ).data;
  },
  GET_TIMESERIES: async function (): Promise<PlatformTimeseries> {
    return (
      await axios.get<PlatformTimeseries>(
        `${API_BASE_URL}${APIRoutes.ADMIN_TIMESERIES}`
      )
    ).data;
  },
  GET_FUNNEL: async function (windowDays = 30): Promise<FunnelResult> {
    return (
      await axios.get<FunnelResult>(
        `${API_BASE_URL}${APIRoutes.ADMIN_FUNNEL}?days=${windowDays}`
      )
    ).data;
  },
  GET_CUSTOMER_TIMESERIES: async function (
    email: string
  ): Promise<CustomerTimeseries> {
    return (
      await axios.get<CustomerTimeseries>(
        `${API_BASE_URL}${APIRoutes.ADMIN_CUSTOMER}/${encodeURIComponent(
          email
        )}/timeseries`
      )
    ).data;
  },
  GET_CUSTOMER_SHOPIFY: async function (
    email: string
  ): Promise<ShopifyCustomerSummary> {
    return (
      await axios.get<ShopifyCustomerSummary>(
        `${API_BASE_URL}${APIRoutes.ADMIN_CUSTOMER}/${encodeURIComponent(
          email
        )}/shopify`
      )
    ).data;
  },
  GET_CUSTOMER_HUBSPOT: async function (
    email: string
  ): Promise<HubspotCustomerSummary> {
    return (
      await axios.get<HubspotCustomerSummary>(
        `${API_BASE_URL}${APIRoutes.ADMIN_CUSTOMER}/${encodeURIComponent(
          email
        )}/hubspot`
      )
    ).data;
  },
  GET_CUSTOMER_HUBSPOT_ENGAGEMENTS: async function (
    email: string,
    options: { limit?: number; offset?: number; type?: string } = {}
  ): Promise<HubspotEngagementListResponse> {
    const qs = new URLSearchParams();
    if (options.limit !== undefined) qs.set("limit", String(options.limit));
    if (options.offset !== undefined) qs.set("offset", String(options.offset));
    if (options.type) qs.set("type", options.type);
    const search = qs.toString();
    return (
      await axios.get<HubspotEngagementListResponse>(
        `${API_BASE_URL}${APIRoutes.ADMIN_CUSTOMER}/${encodeURIComponent(
          email
        )}/hubspot-engagements${search ? `?${search}` : ""}`
      )
    ).data;
  },
  GET_SYNC_OVERVIEW: async function (): Promise<SyncOverview> {
    return (
      await axios.get<SyncOverview>(`${API_BASE_URL}/admin/sync/overview`)
    ).data;
  },
  GET_SYNC_SUGGESTED: async function (
    platform: "shopify" | "hubspot",
    limit = 100,
    offset = 0
  ): Promise<{ rows: SuggestedMatch[] }> {
    return (
      await axios.get<{ rows: SuggestedMatch[] }>(
        `${API_BASE_URL}/admin/sync/${platform}/suggested?limit=${limit}&offset=${offset}`
      )
    ).data;
  },
  GET_SYNC_GROUPS: async function (
    platform: "shopify" | "hubspot",
    limit = 50,
    offset = 0
  ): Promise<{ groups: MatchGroup[] }> {
    return (
      await axios.get<{ groups: MatchGroup[] }>(
        `${API_BASE_URL}/admin/sync/${platform}/groups?limit=${limit}&offset=${offset}`
      )
    ).data;
  },
  GET_SHOPIFY_STATS: async function (): Promise<ShopifyPlatformStats> {
    return (
      await axios.get<ShopifyPlatformStats>(
        `${API_BASE_URL}/admin/sync/shopify/stats`
      )
    ).data;
  },
  GET_HUBSPOT_STATS: async function (): Promise<HubspotPlatformStats> {
    return (
      await axios.get<HubspotPlatformStats>(
        `${API_BASE_URL}/admin/sync/hubspot/stats`
      )
    ).data;
  },
  APPROVE_LINK: async function (linkId: string): Promise<{ ok: boolean }> {
    return (
      await axios.post<{ ok: boolean }>(
        `${API_BASE_URL}/admin/sync/link/${encodeURIComponent(linkId)}/approve`
      )
    ).data;
  },
  REJECT_LINK: async function (linkId: string): Promise<{ ok: boolean }> {
    return (
      await axios.post<{ ok: boolean }>(
        `${API_BASE_URL}/admin/sync/link/${encodeURIComponent(linkId)}/reject`
      )
    ).data;
  },
};
